import { ALL_TOOL_IDS, TOOL_LABELS, isToolId, type ToolId } from '@warehouselogix/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { API_BASE } from '../config';
import { getDeviceContext } from '../device/deviceToken';
import { clearOperator } from '../device/operatorToken';
import { useOperator } from '../device/useOperator';
import { settlePendingWrites } from '../net/floorNet';
import { StatusPill } from '../net/StatusPill';
import { floorAuthHeaders } from '../net/transport';

import './hub.css';

/**
 * The floor hub: one tile per ENABLED tool, from `GET /floor/tools` (the API
 * returns only what the site configuration enables). Until that endpoint
 * answers, every known tool renders so the hub is never a dead end in dev.
 *
 * Pick is special: its three MODES are tiles of their own (the monolith's
 * ttruck / tstage / ttrailer home tiles, PICK_MODE_TILE 12816) — deep links
 * carry the mode so entering a mode goes through the same path a tap does.
 */

interface HubTool {
  id: ToolId;
  label: string;
}

const PICK_MODE_TILES: { mode: string; label: string; hint: string }[] = [
  { mode: 'direct', label: 'Pick to Truck', hint: 'one step — pick and ship' },
  { mode: 'stage', label: 'Pick to Stage', hint: 'pick now, ship later' },
  { mode: 'ship', label: 'Stage to Truck', hint: 'load staged stock and ship' },
];

async function fetchEnabledTools(): Promise<HubTool[]> {
  const base = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
  const response = await fetch(`${base}/floor/tools`, {
    headers: floorAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(`tools list failed (${response.status})`);
  }
  const data = (await response.json()) as { tools?: { id?: unknown; label?: unknown }[] };
  const out: HubTool[] = [];
  for (const t of data.tools ?? []) {
    if (isToolId(t.id)) {
      out.push({ id: t.id, label: typeof t.label === 'string' ? t.label : TOOL_LABELS[t.id] });
    }
  }
  return out;
}

export function HubScreen(): JSX.Element {
  const context = getDeviceContext();
  const operator = useOperator();
  const [tools, setTools] = useState<HubTool[] | null>(null);

  useEffect(() => {
    settlePendingWrites();
    let alive = true;
    fetchEnabledTools()
      .then((list) => {
        if (alive && list.length > 0) {
          setTools(list);
        }
      })
      .catch(() => {
        // Endpoint not up yet (Dev B) or offline: fall back to all tools.
      });
    return () => {
      alive = false;
    };
  }, []);

  const shown = tools ?? ALL_TOOL_IDS.map((id) => ({ id, label: TOOL_LABELS[id] }));

  return (
    <div className="kiosk">
      <header className="kiosk__header">
        <div className="kiosk__dept">WarehouseLogix</div>
        <div className="hub-site">{context !== null ? context.siteName : 'Paired device'}</div>
        {operator !== null && (
          <div className="hub-operator">
            <span className="hub-operator__name">{operator.displayName}</span>
            <button type="button" className="hub-operator__switch" onClick={clearOperator}>
              Switch user
            </button>
          </div>
        )}
      </header>
      <div className="card hub-card">
        <nav className="hub-tiles">
          {shown.map((t) =>
            t.id === 'pick' ? (
              PICK_MODE_TILES.map((m) => (
                <Link
                  key={`pick-${m.mode}`}
                  to={`/tools/pick?mode=${m.mode}`}
                  className="hub-tile hub-tile--pick"
                >
                  <span className="hub-tile__label">{m.label}</span>
                  <span className="hub-tile__hint">{m.hint}</span>
                </Link>
              ))
            ) : (
              <Link key={t.id} to={`/tools/${t.id}`} className="hub-tile">
                <span className="hub-tile__label">{t.label}</span>
              </Link>
            )
          )}
        </nav>
      </div>
      <StatusPill />
    </div>
  );
}
